import { api } from ".";
import {
  ICustomCptVolumeDetail,
  ICustomCptVolumeUploadListItem,
  CustomCptVolumeSpecialty,
} from "../../types/customCptVolume";

type ListResponse = {
  data: ICustomCptVolumeUploadListItem[];
  message?: string;
};

type ItemResponse = {
  data: ICustomCptVolumeUploadListItem;
  message?: string;
};

type DetailResponse = {
  data: ICustomCptVolumeDetail;
  message?: string;
};

type DownloadUrlResponse = {
  url: string;
  message?: string;
};

const customCptVolumeQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getCustomCptVolumes: builder.query<
      ListResponse,
      {
        accountId?: string;
        specialty?: CustomCptVolumeSpecialty;
        payerId?: number | string;
      }
    >({
      query: (params) => ({
        url: "/custom-cpt-volume",
        params,
      }),
      providesTags: ["CustomCptVolume"],
    }),
    getCustomCptVolumeById: builder.query<DetailResponse, string>({
      query: (id) => `/custom-cpt-volume/${id}`,
      providesTags: ["CustomCptVolume"],
    }),
    createCustomCptVolume: builder.mutation<
      ItemResponse,
      {
        file: File;
        accountId: string;
        specialty: CustomCptVolumeSpecialty;
        label: string;
        /** Optional payer scope. Omit for global (applies to all payers). */
        payerId?: number | string | null;
      }
    >({
      query: ({ file, accountId, specialty, label, payerId }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("accountId", accountId);
        formData.append("specialty", specialty);
        formData.append("label", label);
        if (payerId !== undefined && payerId !== null && payerId !== "") {
          formData.append("payerId", String(payerId));
        }
        return {
          url: "/custom-cpt-volume",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["CustomCptVolume"],
    }),
    updateCustomCptVolume: builder.mutation<
      ItemResponse,
      {
        id: string;
        label?: string;
        isActive?: boolean;
      }
    >({
      query: ({ id, label, isActive }) => ({
        url: `/custom-cpt-volume/${id}`,
        method: "PATCH",
        body: {
          ...(label !== undefined ? { label } : {}),
          ...(isActive !== undefined ? { isActive } : {}),
        },
      }),
      invalidatesTags: ["CustomCptVolume"],
    }),
    deleteCustomCptVolume: builder.mutation<ItemResponse, string>({
      query: (id) => ({
        url: `/custom-cpt-volume/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomCptVolume"],
    }),
    getCustomCptVolumeDownloadUrl: builder.query<DownloadUrlResponse, string>({
      query: (id) => `/custom-cpt-volume/${id}/download-url`,
    }),
  }),
});

export const {
  useGetCustomCptVolumesQuery,
  useGetCustomCptVolumeByIdQuery,
  useCreateCustomCptVolumeMutation,
  useUpdateCustomCptVolumeMutation,
  useDeleteCustomCptVolumeMutation,
  useLazyGetCustomCptVolumeDownloadUrlQuery,
} = customCptVolumeQueries;
